import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Clock, FileText, Phone, Mail, Shield, X, AlertTriangle, Download } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_INFO = {
  draft: { label: 'Pending', icon: Clock, color: 'text-gray-600' },
  sent: { label: 'Awaiting Signature', icon: Clock, color: 'text-blue-600' },
  viewed: { label: 'Awaiting Signature', icon: Clock, color: 'text-yellow-600' },
  accepted: { label: 'Signed & Active', icon: Check, color: 'text-green-600' },
  declined: { label: 'Declined', icon: X, color: 'text-red-600' },
};

export default function PublicContractPage() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/contracts/${token}`)
      .then(r => { if (!r.ok) throw new Error('Contract not found'); return r.json(); })
      .then(data => setContract(data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!agreed) { alert('Please agree to the terms before signing.'); return; }
    setAccepting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contracts/${token}/accept`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        setContract(c => ({ ...c, status: 'accepted', acceptedAt: data.acceptedAt, acceptedIp: data.acceptedIp }));
      } else {
        alert(data.detail || 'Could not accept contract');
      }
    } catch { alert('Network error'); }
    setAccepting(false);
  };

  const handleDecline = async () => {
    if (!window.confirm('Are you sure you want to decline this contract?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/contracts/${token}/decline`, { method: 'POST' });
      if (res.ok) setContract(c => ({ ...c, status: 'declined' }));
    } catch { alert('Network error'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center"><FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-500 text-lg">{error}</p></div>
    </div>
  );

  const st = STATUS_INFO[contract.status] || STATUS_INFO.sent;
  const StIcon = st.icon;
  const canAct = ['sent', 'viewed'].includes(contract.status);

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4" data-testid="public-contract-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-700 to-teal-700 rounded-t-2xl p-8 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold mb-1">Virtual CFO Consultancy</h1>
              <p className="text-emerald-200 text-sm">Consultants India Private Limited</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-emerald-200">Service Agreement</p>
              <p className="text-lg font-bold" data-testid="contract-number">{contract.contractNumber}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="bg-white rounded-b-2xl shadow-lg">
          {/* Status Bar */}
          <div className={`px-8 py-3 border-b flex items-center gap-2 ${st.color}`}>
            <StIcon className="w-4 h-4" />
            <span className="font-medium text-sm">Status: {st.label}</span>
            {contract.status === 'accepted' && (
              <span className="ml-auto text-xs text-gray-400">
                Signed on {new Date(contract.acceptedAt).toLocaleDateString('en-IN')} | IP: {contract.acceptedIp}
              </span>
            )}
          </div>

          <div className="p-8">
            {/* Client Info */}
            <div className="mb-8">
              <p className="text-sm text-gray-500 mb-1">Prepared for</p>
              <p className="text-xl font-bold text-gray-900" data-testid="contract-client-name">{contract.leadName}</p>
              {contract.leadCompany && <p className="text-gray-600">{contract.leadCompany}</p>}
              <p className="text-sm text-gray-500">{contract.leadEmail}</p>
            </div>

            {/* Contract Terms Summary */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Duration</p>
                <p className="font-semibold text-sm" data-testid="contract-duration">{contract.contractDuration}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Payment Terms</p>
                <p className="font-semibold text-sm" data-testid="contract-payment-terms">{contract.paymentTerms}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Auto-Renewal</p>
                <p className="font-semibold text-sm">{contract.autoRenew ? 'Yes' : 'No'}</p>
              </div>
            </div>

            {/* Services Table */}
            <table className="w-full mb-6" data-testid="contract-services-table">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-3">Service</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-3">Description</th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase pb-3">Monthly</th>
                </tr>
              </thead>
              <tbody>
                {(contract.services || []).map((svc, i) => (
                  <tr key={`service-row-${i}`} className="border-b border-gray-100" data-testid={`contract-service-row-${i}`}>
                    <td className="py-3 font-medium text-gray-900 text-sm">{svc.name}</td>
                    <td className="py-3 text-gray-500 text-sm">{svc.description}</td>
                    <td className="py-3 text-right font-semibold text-sm">{"\u20B9"}{svc.amount?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Monthly Total */}
            <div className="bg-gray-50 rounded-xl p-5 mb-8">
              <div className="flex justify-between text-lg">
                <span className="font-bold text-gray-900">Monthly Total</span>
                <span className="font-bold text-emerald-600" data-testid="contract-total">{"\u20B9"}{contract.monthlyTotal?.toLocaleString()}/mo</span>
              </div>
            </div>

            {/* SLA Terms */}
            {contract.slaTerms && (
              <div className="mb-8">
                <h3 className="font-semibold text-gray-900 mb-2">Service Level Agreement</h3>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 whitespace-pre-wrap" data-testid="contract-sla">{contract.slaTerms}</p>
                </div>
              </div>
            )}

            {/* Notes */}
            {contract.notes && (
              <div className="mb-8 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">{contract.notes}</p>
              </div>
            )}

            {/* Accept/Decline Actions */}
            {canAct && (
              <div className="space-y-4" data-testid="contract-actions">
                {/* Agreement Checkbox */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                      className="w-5 h-5 mt-0.5 rounded border-gray-300 text-emerald-600" data-testid="agree-checkbox" />
                    <span className="text-sm text-gray-700">
                      I, <strong>{contract.leadName}</strong>, have reviewed and agree to the terms of this Service Agreement, 
                      including the scope of services, payment terms ({contract.paymentTerms}), and contract duration ({contract.contractDuration}). 
                      I understand that clicking "Accept & Sign" constitutes a legally binding digital signature.
                    </span>
                  </label>
                </div>

                <div className="flex gap-3">
                  <Button onClick={handleAccept} disabled={accepting || !agreed}
                    className={`flex-1 py-6 text-base ${agreed ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-300 cursor-not-allowed'}`}
                    data-testid="accept-contract-btn">
                    <Check className="w-5 h-5 mr-2" /> {accepting ? 'Processing...' : 'Accept & Sign'}
                  </Button>
                  <Button onClick={handleDecline} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 py-6" data-testid="decline-contract-btn">
                    <X className="w-5 h-5 mr-2" /> Decline
                  </Button>
                </div>

                <p className="text-xs text-gray-400 text-center flex items-center justify-center gap-1">
                  <Shield className="w-3 h-3" /> Your IP address and timestamp will be recorded for audit purposes
                </p>
              </div>
            )}

            {contract.status === 'accepted' && (
              <div className="bg-emerald-50 rounded-xl p-6 text-center border border-emerald-200" data-testid="contract-accepted-banner">
                <Check className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-emerald-800 mb-1">Contract Signed!</h3>
                <p className="text-emerald-700 mb-3">Our team will begin onboarding within 24 hours.</p>
                <div className="text-xs text-emerald-600 space-y-1 mb-4">
                  <p>Signed on: {new Date(contract.acceptedAt).toLocaleString('en-IN')}</p>
                  <p>IP Address: {contract.acceptedIp}</p>
                </div>
                <Button onClick={() => {
                  window.open(`${BACKEND_URL}/api/contracts/${token}/download-pdf`, '_blank');
                }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="download-accepted-pdf-btn">
                  <Download className="w-4 h-4 mr-2" /> Download Signed Contract PDF
                </Button>
              </div>
            )}

            {contract.status === 'declined' && (
              <div className="bg-red-50 rounded-xl p-6 text-center border border-red-200" data-testid="contract-declined-banner">
                <X className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-red-800 mb-1">Contract Declined</h3>
                <p className="text-red-600">Changed your mind? Contact us anytime.</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 rounded-b-2xl px-8 py-5 border-t flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> +91 7977789741</span>
              <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> info@nsrm.in</span>
            </div>
            <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> 256-bit encrypted</span>
          </div>
        </div>
      </div>
    </div>
  );
}
